import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { ContentItemViewNodesType } from '../types/contentItemViewNode.type'
import type { DataNode, NodeType, PTDesignationKey } from '../types/types'
import { shouldHideBODPill } from '../utils/bodUtils'

import { ElementType } from 'api/graphQL/reactiveVars/element'
/** Utility class for tree data mapping operations */
export class TreeDataMapper<
    TContentItemViewNode extends Partial<ContentItemViewNodesType>
> {
    public contentItemViewNodes: TContentItemViewNode[]
    private isOMorSMMPage: boolean
    private activeTab: number
    private isProjectProductFamily: boolean
    private elementType: string
    private isElementProductFamily: boolean

    constructor(
        contentItemViewNodes: TContentItemViewNode[],
        isOMorSMMPage: boolean,
        activeTab: number = 0,
        isProjectProductFamily: boolean = false,
        elementType: string = ''
    ) {
        this.contentItemViewNodes = contentItemViewNodes
        this.isOMorSMMPage = isOMorSMMPage
        this.activeTab = activeTab
        this.isProjectProductFamily = isProjectProductFamily
        this.elementType = elementType
        this.isElementProductFamily = elementType === ElementType.PRODUCTFAMILY
    }

    /**
     * Finds child node IDs in Document Template View
     * @param parentKey - Parent node key
     * @returns Array of child node keys
     */
    findChildren(parentKey: string): React.Key[] {
        // Bug 2675141: match parent by id only — input is already deduped by id
        // upstream, and a stricter filter hides nested parents after demote.
        const matchesContent = (content: TContentItemViewNode) =>
            content?.id === parentKey

        const contentItemId = this.contentItemViewNodes.find(matchesContent)
            ?.dtvItemId

        if (!contentItemId) return []

        const childIds: React.Key[] = []

        for (const contentItemNode of this.contentItemViewNodes) {
            if (!contentItemNode.id) continue

            const isChildNode =
                contentItemNode?.refBodItemId === '' &&
                contentItemNode.dtvContentItemParentId === parentKey
            const isDirectChildNode =
                contentItemNode.dtvParentId === contentItemId &&
                contentItemNode.dtvContentItemParentId === null &&
                contentItemNode?.refBodItemId === ''
            const isBodChildNode =
                contentItemNode?.refBodItemId === contentItemId

            if (isChildNode || isDirectChildNode || isBodChildNode) {
                childIds.push(contentItemNode.id as string)
            }
        }

        return childIds
    }

    /**
     * Converts ContentItem to DataNode
     * @param contentItem - ContentItem to convert
     * @param rootArticleId - Optional ID of the parent article, used for tracking
     * @param isDescendantOfProductTypeOrGroup - Optional flag indicating if this node is a descendant of a product type/group
     * @param visitedNodes - Set of visited node IDs to prevent circular references
     * @returns DataNode object
     */
    buildTreeNodes(
        contentItemView: TContentItemViewNode,
        rootArticleId?: string,
        isDescendantOfProductTypeOrGroup?: boolean,
        visitedNodes: Set<string> = new Set()
    ): DataNode {
        const nodeId = contentItemView.id ?? ''
        const order = contentItemView.order ?? 0
        const contentItemNodes = contentItemView.elements ?? []
        const isArticleLevel =
            (contentItemView.textType as NodeType) === NODE_TYPES.Article
        const isProductGroup = contentItemView.isProductGroup
        const isProductType = contentItemView.isProductType

        // Add current node to visited set
        const newVisitedNodes = new Set(visitedNodes)
        if (nodeId) {
            newVisitedNodes.add(nodeId)
        }

        // determine the effective rootArticleId
        // if this is an article node and no rootArticleId exists yet, use this node's ID
        let currentRootArticleId = rootArticleId
        if (isArticleLevel && !currentRootArticleId) {
            currentRootArticleId = nodeId
        }

        // determine if this node or its descendants should have isProductTypeContent flag
        // either it's already a descendant of a product type/group OR
        // this is an article node that is a product group or product type
        const isProductTypeContent =
            isDescendantOfProductTypeOrGroup ||
            (isArticleLevel && (isProductGroup || isProductType))

        const children =
            nodeId && visitedNodes.has(nodeId)
                ? []
                : (contentItemView?.id &&
                      this.buildChildren(
                          contentItemView.id,
                          currentRootArticleId,
                          isProductTypeContent,
                          newVisitedNodes
                      )) ||
                  []
        // if descendant of product type group we check if it is added to project
        const isHidden = this.isHiddenContentItem(
            contentItemView,
            isDescendantOfProductTypeOrGroup
        )
        const isDisabled = this.isDisabledContentItem(
            contentItemView,
            isDescendantOfProductTypeOrGroup
        )
        return {
            order,
            children,
            key: nodeId,
            contentItemNodes: contentItemNodes as TContentItemViewNode[],
            contentItemView: contentItemView,
            type: contentItemView.textType as NodeType,
            parentComments: contentItemView.parentComments,
            specifierNotes: contentItemView.comments,
            isHidden: this.isOMorSMMPage
                ? contentItemView.isDeactivated ?? false
                : isHidden || (contentItemView.isDeactivated ?? false),
            isDisabled: isDisabled,
            isDeactivated: contentItemView.isDeactivated ?? false,
            isProductGroup: isProductGroup,
            isProductType: isProductType,
            isUserAdded: contentItemView.isUserAdded,
            rootArticleId: currentRootArticleId,
            isProductTypeContent: isProductTypeContent,
            isManufacturers: contentItemView.isManufacturers,
            isBOD: contentItemView.isBOD,
            isCustomManufacturer: contentItemView.isCustomManufacturer,
            pTDesignationKey: contentItemView.pTDesignationKey as PTDesignationKey | undefined,
        }
    }

    /**
     * Builds child nodes recursively
     * @param parentKey - Parent node key
     * @param rootArticleId - Optional ID of the parent article, used for tracking
     * @param isDescendantOfProductTypeOrGroup - Optional flag indicating if children are descendants of a product type/group
     * @param visitedNodes - Set of visited node IDs to prevent circular references
     * @returns Array of child DataNodes
     */
    buildChildren(
        parentKey: string,
        rootArticleId?: string,
        isDescendantOfProductTypeOrGroup?: boolean,
        visitedNodes: Set<string> = new Set()
    ): DataNode[] {
        let childIds = this.findChildren(parentKey)

        const childNodes = childIds
            .map((id) =>
                this.contentItemViewNodes.find((node) => node.id === id)
            )
            .filter((node): node is TContentItemViewNode => node !== undefined)
            .map((node) =>
                this.buildTreeNodes(
                    node,
                    rootArticleId,
                    isDescendantOfProductTypeOrGroup,
                    visitedNodes
                )
            )

        return childNodes.sort((a, b) => a.order - b.order) as DataNode<
            TContentItemViewNode
        >[]
    }

    /**
     * Checks if a node key is linked to a DTV item
     * @param node - DataNode to check
     * @returns True if linked/found, false otherwise
     */
    isLinkedArticle(node: DataNode): boolean {
        return (
            node.type === NODE_TYPES.Article &&
            this.contentItemViewNodes.some(
                (content) => content?.id === node.key
            )
        )
    }

    /**
     * Filters out hidden content items
     * @param contentItems - Array of content items
     * @returns Array of content items without hidden items
     */
    removeHiddenContent(
        contentItems: TContentItemViewNode[]
    ): TContentItemViewNode[] {
        return contentItems.filter((ci) => ci.isHidden === false) // filter out hidden content items)
    }

    /**
     * Checks if a content item is hidden.
     * This prevents recalculation after structural changes like optimistic updates.
     * @param contentItemView - Content item to check
     * @returns True if hidden, false otherwise
     */
    isHiddenContentItem(
        contentItemView: TContentItemViewNode,
        isDescendantOfProductTypeOrGroup?: boolean
    ): boolean {
        // If the content item has an explicit isHidden value, preserve it
        if (contentItemView.isHidden !== undefined) {
            return contentItemView.isHidden
        }

        // Otherwise, calculate based on parent relationships
        const isParentHidden = this.contentItemViewNodes
            .filter(
                (civ) =>
                    civ.id === contentItemView.dtvContentItemParentId ||
                    civ.dtvItemId === contentItemView.dtvParentId ||
                    civ.dtvItemId === contentItemView.refBodItemId
            )
            .some((civ) => civ.isHidden)
        const dtvItemId = this.contentItemViewNodes.find(
            (civ) => civ.id === contentItemView.id && civ.isHidden
        )?.dtvItemId

        const shouldHideBODPillResult = shouldHideBODPill(
            contentItemView,
            this.contentItemViewNodes,
            dtvItemId
        )

        if (shouldHideBODPillResult !== null) {
            return shouldHideBODPillResult
        }

        return !!dtvItemId || isParentHidden
    }

    /**
     * Checks if a content item should be disabled (not hidden, just disabled styling)
     * @param contentItemView - Content item to check
     * @param isDescendantOfProductTypeOrGroup - Whether this is a descendant of product type/group
     * @returns True if disabled, false otherwise
     */
    isDisabledContentItem(
        contentItemView: TContentItemViewNode,
        isDescendantOfProductTypeOrGroup?: boolean
    ): boolean {
        const isSharedContent =
            contentItemView?.isSharedProperty ||
            contentItemView?.isSharedProductTypeProperty

        // If the selected node is a family on products tab and content is a descendant of a product type or group, check if it is added to project
        // But if the content is shared, it should not be disabled
        // Only disable if the content is product-type-specific (isForProductType or isProductType: true)
        const shouldDisableProductType =
            this.isElementProductFamily &&
            this.activeTab === 2 &&
            isDescendantOfProductTypeOrGroup &&
            !contentItemView?.isAddedToProject &&
            !isSharedContent &&
            (contentItemView?.isForProductType ||
                contentItemView?.isProductType)

        return !!shouldDisableProductType
    }

    /**
     * Checks if a content item is a shared PT property
     * @param contentItem - Content item to check
     * @returns True if shared PT property, false otherwise
     */
    isSharedPTProperty(contentItemView: TContentItemViewNode): boolean {
        const dtvItemId = this.contentItemViewNodes.find(
            (civ) => civ.id === contentItemView.id && !civ.isSharedProperty
        )?.dtvItemId

        return !!dtvItemId
    }
}
